import type { ConversationResponse } from '@blazeteams/sdk';
import { readSystemEvent } from './system-event-presentation';
import { readTemplatePresentation, type TemplatePresentation } from './template-presentation';

interface SystemConversationEventPreview {
  id: string;
  conversation_id: string;
  message_type: 'system';
  created_at: string;
  metadata: { blaze_system_event: Record<string, unknown> };
}
interface TemplateConversationEventPreview {
  id: string;
  conversation_id: string;
  message_type: 'template';
  created_at: string;
  direction: 'inbound' | 'outbound';
  content?: string;
  template_presentation?: TemplatePresentation;
  metadata?: never;
}
export type ConversationEventPreview = SystemConversationEventPreview | TemplateConversationEventPreview;
export type ConversationListRow = Omit<ConversationResponse, 'last_message_event'> & { last_message_event?: ConversationEventPreview | null };
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

/** Optional projection only. Unknown versions/invalid presentation data fall
 * back to literal legacy preview; foreign-resource identity fails at the caller. */
export function projectConversationEventPreview(value: unknown, conversationId: string): ConversationEventPreview | null {
  if (!record(value) || value.conversation_id !== conversationId || typeof value.id !== 'string' || !value.id.trim() || typeof value.created_at !== 'string' || !Number.isFinite(new Date(value.created_at).getTime())) return null;
  if (value.message_type === 'template') {
    if (value.direction !== 'inbound' && value.direction !== 'outbound') return null;
    const presentation = readTemplatePresentation({ direction: value.direction, message_type: 'template', content: typeof value.content === 'string' ? value.content : undefined, template_presentation: value.template_presentation });
    return { id: value.id, conversation_id: conversationId, message_type: 'template', created_at: value.created_at, direction: value.direction,
      ...(presentation ? { content: presentation.source_content, template_presentation: presentation } : {}) };
  }
  if (value.message_type !== 'system') return null;
  const event = readSystemEvent({message_type:'system',metadata:value.metadata});
  if (!event) return null;
  const marker = {version:1,event:event.event,actor:event.actor,...(event.target_name ? {target_name:event.target_name}:{}),...(event.rating !== undefined ? {rating:event.rating,scale_max:event.scale_max??null}:{})};
  return {id:value.id,conversation_id:conversationId,message_type:'system',created_at:value.created_at,metadata:{blaze_system_event:marker}};
}
